import React, { useState, useEffect, useRef } from 'react';
import { IconButton, Stack, Typography, Box, Grid2, TextField, Button, Link, Divider } from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ReservationDetail from './ReservationDetail';
import ReservationNote from './ReservationNote';
import UpdateReservation from './UpdateReservation';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useFetchReservationDetailsQuery, useLazyGetOccupancyDataQuery } from '../../../api/endpoints/apiConfig';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { OccupancyData } from '../../../models/OccupancyData';
import CircularProgress from '@mui/material/CircularProgress';
import CustomAccordion from '../../../components/Accordian/accordian';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { AccordionSummary, AccordionDetails } from '@mui/material';
import { AircallNofity } from '../../../components/Common/notify';
type Props = {}

const ManageReservation = (props: Props) => {
    const { facilityId, externalId } = useParams<{ facilityId: string; externalId: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const { data } = location.state || {};
    // Ensure facilityId and dealId are numbers
    const numericFacilityId = Number(facilityId);
    const numericDealId = Number(externalId);
    // Fetch reservation details with numeric IDs
    const { data: reservationDetails, isLoading, refetch } = useFetchReservationDetailsQuery({
        facilityId: numericFacilityId,
        dealId: numericDealId,
    }, { 
        skip: isNaN(numericFacilityId) || isNaN(numericDealId)  // Skip the query if IDs are invalid
    });
    const unittypeid = reservationDetails?.deal.unit.external_unit_type_id;
   
    const handleUpdate = () => {  
        refetch();
    };

    // Lookup Occupant functionality
    const [isLookup, setIsLookup] = useState(false);
    const [searchdata, setSearchdata] = useState({
        firstnamesearch: '',
        lastnamesearch: '',
        accesscodesearch: '',
        emailsearch: '',
        phonesearch: '',
    });
    const [gridData, setGridData] = useState<OccupancyData[]>([]);
    const [isgrid, setIsgrid] = useState(false);
    const [selectedOccupant, setSelectedOccupant] = useState<any>(null);
    const firstNameInputRef = useRef<HTMLInputElement>(null);

    // Grid Loaded based on the search value
    const [trigger, { data: occupancyData, isFetching }] = useLazyGetOccupancyDataQuery();

    const handleLookupTenantSearch = async () => {
        if (searchdata.emailsearch.length < 1 && searchdata.firstnamesearch.length < 1 && searchdata.lastnamesearch.length < 1 && searchdata.accesscodesearch.length < 1 && searchdata.phonesearch.length < 1) {
            setIsgrid(false)
            AircallNofity({ type: 'error', Msg: 'Please enter atleast one search criteria' });
        }
        else {
            setIsgrid(true)
            try {
                await trigger({
                    facilityid: numericFacilityId,
                    email: searchdata.emailsearch.toLowerCase(),
                    firstname: searchdata.firstnamesearch.toLowerCase(),
                    lastname: searchdata.lastnamesearch.toLowerCase(),
                    accesscode: searchdata.accesscodesearch,
                    phone: searchdata.phonesearch
                });
            } catch (err) {
                console.error('Error fetching occupancy data:', err);
            }
        }
    };

    useEffect(() => {
        if (occupancyData) {
            setGridData(occupancyData);
        }
    }, [occupancyData]);

    //Search Handle change
    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setSearchdata({
            ...searchdata,
            [name]: value,
        });
    }

    const handleLookupTenant = (): void => {
        setIsLookup(true);
        requestAnimationFrame(() => {
            const element = document.getElementById('occupant-info');
            if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
                if (firstNameInputRef.current) {
                    firstNameInputRef.current.focus();
                }
            }
        });
    };

    const formatPhoneNumber = (phone: string): string => {
        if (phone.length < 10) return 'Invalid Number'; // Check for valid phone number length
        return phone.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
    };

    const SetOccupancyData = (params: any) => {
        const data = params.row;
        setSelectedOccupant({
            firstname: data?.first_name,
            lastname: data?.last_name,
            phNum: formatPhoneNumber(data?.phone_number),
            email: data?.email,
            occupancyid: data?.id,
            facility_id: data?.facility_id,
            external_id: (data?.external_id).toString(),
        });
        setIsLookup(false);
        setIsgrid(false);
        setSearchdata({
            ...searchdata,
            firstnamesearch: '',
            lastnamesearch: '',
            accesscodesearch: '',
            emailsearch: '',
            phonesearch: ''
        });
    };

    const CustomNoResultsOverlay = () => (
        <Box textAlign="center" p={2}>
            <Typography variant="h6">No Data Available</Typography>
        </Box>
    );

    // columns def
    const colDefs: GridColDef[] = [
        { field: 'first_name', headerName: 'First Name', minWidth: 120 },
        { field: 'last_name', headerName: 'Last Name', minWidth: 120 },
        { field: 'address', headerName: 'Address', minWidth: 150 },
        { field: 'access_code', headerName: 'Access Code', minWidth: 115, filterable: false, sortable: false },
        {
            field: "phone_number",
            headerName: "Phone",
            minWidth: 130,
            headerAlign: 'center', align: 'center',
            flex: 1,
            valueGetter: (params) => {
                const phone = params; // Accessing the phone directly
                return phone ? formatPhoneNumber(phone) : 'N/A'; // Assuming formatPhoneNumber is a valid function
            },
        },
        { field: 'email', headerName: 'Email', minWidth: 210 },
        {
            field: 'action',
            headerName: 'Action',
            flex: 1,
            width: 50,
            filterable: false,
            sortable: false,
            renderCell: (params) => (
                <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                    <Button size="small" variant="contained" onClick={() => SetOccupancyData(params)}>select</Button>
                </div>
            ),
        },
    ];
    return (
        <>
            {isNaN(numericFacilityId) || isNaN(numericDealId) ? (
                <div>Error: Invalid facility or deal ID.</div>
            ) : (
                <>
                    <Stack direction='row' spacing={1} alignItems={'center'} my={3}>
                        <IconButton onClick={() => navigate(`/Dashboard/${numericFacilityId}`)}>
                            <ArrowBackRoundedIcon sx={{ color: 'text.secondary' }} />
                        </IconButton>
                        <Typography variant='h3' color='text.secondary'>
                            Back to facility page
                        </Typography>
                    </Stack>
                    {/* Display loading or data content based on query state */}
                    {isLoading && <div>Loading...</div>}
                    {reservationDetails ? (
                        <>
                            <Box my={2}>
                                <ReservationDetail facilityId={numericFacilityId} dealId={numericDealId} onUpdate={handleUpdate} />
                            </Box>
                            <Box my={2}>
                                <ReservationNote facilityId={numericFacilityId} dealId={numericDealId} />
                            </Box>
                            <Box my={2}>
                                <UpdateReservation 
                                    facilityId={numericFacilityId} 
                                    dealId={numericDealId} 
                                    onUpdate={handleUpdate} 
                                    data={data} 
                                    unittypeid={unittypeid}
                                    selectedOccupant={selectedOccupant}
                                    onLookupTenant={handleLookupTenant}
                                />
                            </Box>
                            
                            {isLookup ? (
                                <Box my={2}>
                                    <CustomAccordion defaultExpanded>
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon />}
                                            aria-controls="panel1-content"
                                            id="panel1-header"
                                        >
                                            <div id="occupant-info">
                                                <Typography variant="h3" color="text.secondary">Search Occupant
                                                </Typography>
                                            </div>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Box py={2} style={{ width: '100%', height: '100px' }}>
                                                <Grid2 size={{ xs: 12, md: 12 }}>
                                                    <TextField
                                                        id="First-namesearch"
                                                        name="firstnamesearch"
                                                        label="First Name"
                                                        variant="outlined"
                                                        size="small"
                                                        value={searchdata.firstnamesearch}
                                                        onChange={handleSearchChange}
                                                        onKeyDown={(e) => e.key === 'Enter' && handleLookupTenantSearch()}
                                                        sx={{ width: '250px', marginRight: '10px', marginBottom: '16px' }}
                                                        inputRef={firstNameInputRef}
                                                    />
                                                    <TextField
                                                        id="last-namesearch"
                                                        name="lastnamesearch"
                                                        label="Last Name"
                                                        variant="outlined"
                                                        size="small"
                                                        value={searchdata.lastnamesearch}
                                                        onChange={handleSearchChange}
                                                        onKeyDown={(e) => e.key === 'Enter' && handleLookupTenantSearch()}
                                                        sx={{ width: '250px', marginRight: '10px', marginBottom: '16px' }}
                                                    />
                                                    <TextField
                                                        id="email-search"
                                                        name="emailsearch"
                                                        label="Email"
                                                        variant="outlined"
                                                        size="small"
                                                        value={searchdata.emailsearch}
                                                        onChange={handleSearchChange}
                                                        onKeyDown={(e) => e.key === 'Enter' && handleLookupTenantSearch()}
                                                        sx={{ width: '350px', marginRight: '10px', marginBottom: '16px' }}
                                                    />
                                                </Grid2>
                                                <Grid2 size={{ xs: 12, md: 12 }}>
                                                    <TextField
                                                        id="accesscode-search"
                                                        name="accesscodesearch"
                                                        label="Access Code"
                                                        variant="outlined"
                                                        size="small"
                                                        value={searchdata.accesscodesearch}
                                                        onChange={handleSearchChange}
                                                        onKeyDown={(e) => e.key === 'Enter' && handleLookupTenantSearch()}
                                                        sx={{ width: '250px', marginRight: '10px', marginBottom: '16px' }}
                                                    />
                                                    <TextField
                                                        id="phone-search"
                                                        name="phonesearch"
                                                        label="Phone"
                                                        variant="outlined"
                                                        size="small"
                                                        value={searchdata.phonesearch}
                                                        onChange={handleSearchChange}
                                                        onKeyDown={(e) => e.key === 'Enter' && handleLookupTenantSearch()}
                                                        sx={{ width: '250px', marginRight: '10px', marginBottom: '16px' }}
                                                    />
                                                    <Button
                                                        variant='contained'
                                                        size='large'
                                                        sx={{ ml: 2 }}
                                                        onClick={handleLookupTenantSearch}
                                                    >
                                                        Search
                                                    </Button>
                                                </Grid2>
                                            </Box>
                                            <Box sx={{ height: '30px' }} />
                                            {isgrid && (
                                                <>
                                                    <Typography variant="h3" color="text.secondary">
                                                        Pick an Occupant
                                                    </Typography>
                                                    <Box py={2} sx={{ width: '100%' }}>
                                                        {isFetching ? (
                                                            <Box
                                                                sx={{
                                                                    display: "flex",
                                                                    justifyContent: "center",
                                                                    alignItems: "center",
                                                                    height: "70vh",
                                                                }}
                                                            >
                                                                <CircularProgress />
                                                            </Box>
                                                        ) : (
                                                            <DataGrid
                                                                rows={gridData}
                                                                columns={colDefs}
                                                                disableColumnMenu
                                                                slots={{
                                                                    noResultsOverlay: CustomNoResultsOverlay,
                                                                }}
                                                                hideFooterPagination  // This will hide the pagination footer
                                                                autoHeight  // This will adjust the grid height to fit all rows, removing the need for scrolling
                                                            />
                                                        )}
                                                    </Box>
                                                </>
                                            )}
                                        </AccordionDetails>
                                    </CustomAccordion>
                                </Box>
                            ) : null}
                        </>
                    ) : (
                        !isLoading && <div>No reservation details available.</div>
                    )}
                </>
            )}
        </>
    );
}

export default ManageReservation;
